import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  fetchAwaitingPayments,
  setPaymentFollowupNote,
  type AwaitingPaymentRow,
  type AwaitingPaymentsResponse,
} from "../api.js";

/**
 * Awaiting Payments — Andzelika's day-to-day money-chasing view.
 *
 * Opens as a full-screen overlay from the pipeline top bar. Two
 * sections: past unpaid (outstanding) + future booked. Each row shows
 * the customer, scheduled date+time, amount due, expected payment
 * type, tech assigned, last-contacted timestamp, and an inline-edit
 * follow-up note. Open Card links back into the pipeline-card slideout.
 *
 * No filtering / sorting in v1 — the row count is small enough that
 * scrolling beats UI bloat.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onOpenCard: (cardId: number) => void;
}

function fmtGbp(n: number): string {
  return `£${Math.abs(n).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDateOnly(iso: string | null): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("en-GB");
}

export function AwaitingPaymentsPanel({ open, onClose, onOpenCard }: Props): JSX.Element | null {
  const q = useQuery({
    queryKey: ["awaitingPayments"],
    queryFn: fetchAwaitingPayments,
    enabled: open,
    refetchOnWindowFocus: false,
  });

  if (!open) return null;
  const data: AwaitingPaymentsResponse | null = q.data ?? null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] flex items-stretch justify-end bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.aside
          className="flex h-full w-full max-w-[1280px] flex-col bg-ink-900 shadow-2xl"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex items-baseline justify-between gap-4 border-b border-ink-700 bg-ink-900/95 px-6 py-4">
            <div>
              <h2 className="font-display text-2xl font-normal tracking-[-0.02em] text-ink-50">
                Awaiting Payment
              </h2>
              <p className="mt-0.5 text-[12px] text-ink-300">
                Past unpaid + future booked. Outstanding only — paid jobs don&apos;t appear.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-ink-500 px-3 py-1.5 text-[12px] text-ink-100 hover:border-slack-aubergineLight hover:text-ink-50"
            >
              Close
            </button>
          </header>

          <div className="scrollbar-slim flex-1 overflow-y-auto px-6 py-4">
            {q.isLoading ? (
              <div className="text-[13px] text-ink-200">Loading…</div>
            ) : q.error || !data ? (
              <div className="text-[13px] text-red-300">
                Couldn&apos;t load awaiting payments. Reopen the panel to retry.
              </div>
            ) : (
              <>
                <Section
                  title="Past jobs — outstanding"
                  subtitle="Already scheduled (or schedule passed) and still has money owed."
                  rows={data.outstanding.rows}
                  count={data.outstanding.count}
                  total={data.outstanding.totalOutstanding}
                  onOpenCard={onOpenCard}
                  pastTint
                />
                <div className="mt-8">
                  <Section
                    title="Future jobs — expected payments"
                    subtitle="Scheduled ahead of today. Payment will be due on or after the visit."
                    rows={data.future.rows}
                    count={data.future.count}
                    total={data.future.totalOutstanding}
                    onOpenCard={onOpenCard}
                    pastTint={false}
                  />
                </div>
              </>
            )}
          </div>
        </motion.aside>
      </motion.div>
    </AnimatePresence>
  );
}

interface SectionProps {
  title: string;
  subtitle: string;
  rows: AwaitingPaymentRow[];
  count: number;
  total: number;
  onOpenCard: (cardId: number) => void;
  /** Past unpaid rows get a subtle amber/red cue on the amount column. */
  pastTint: boolean;
}

function Section({ title, subtitle, rows, count, total, onOpenCard, pastTint }: SectionProps): JSX.Element {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-ink-50">
            {title}
            <span className="ml-2 rounded bg-ink-700 px-1.5 py-0.5 text-[11px] tabular-nums text-ink-100">
              {count}
            </span>
          </h3>
          <p className="mt-0.5 text-[12px] text-ink-300">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-300">Total</div>
          <div
            className={`text-[18px] font-semibold tabular-nums ${
              pastTint ? "text-orange-300" : "text-ink-50"
            }`}
          >
            {fmtGbp(total)}
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-ink-700 bg-ink-900/40 px-4 py-6 text-center text-[13px] text-ink-300">
          Nothing here. {pastTint ? "All past jobs paid." : "No future bookings with outstanding balance."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-ink-700">
          <table className="w-full text-left text-[12px] text-ink-100">
            <thead className="bg-ink-800/80 text-[11px] uppercase tracking-[0.10em] text-ink-200">
              <tr>
                <th className="px-3 py-2 font-medium">Date / time</th>
                <th className="px-3 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Tech</th>
                <th className="px-3 py-2 font-medium">Expected</th>
                <th className="px-3 py-2 font-medium">Last contact</th>
                <th className="px-3 py-2 font-medium">Follow-up note</th>
                <th className="px-3 py-2 text-right font-medium">Outstanding</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-700">
              {rows.map((r) => (
                <Row key={r.vid} row={r} onOpenCard={onOpenCard} pastTint={pastTint} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface RowProps {
  row: AwaitingPaymentRow;
  onOpenCard: (cardId: number) => void;
  pastTint: boolean;
}

function Row({ row, onOpenCard, pastTint }: RowProps): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.paymentFollowupNote ?? "");
  const saveM = useMutation({
    mutationFn: async (note: string) => setPaymentFollowupNote(row.vid, note.trim() || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["awaitingPayments"] });
      setEditing(false);
    },
  });

  const sched =
    row.scheduledDate && row.scheduledTime
      ? `${fmtDateOnly(row.scheduledDate)} · ${row.scheduledTime}`
      : fmtDateOnly(row.scheduledDate);

  const expected =
    row.paymentTypeLabel || row.paymentStatusLabel
      ? `${row.paymentTypeLabel ?? "—"}${
          row.paymentStatusLabel ? ` · ${row.paymentStatusLabel}` : ""
        }`
      : "Not set";

  return (
    <tr className="align-top hover:bg-ink-800/40">
      <td className="px-3 py-2 tabular-nums text-ink-100">{sched}</td>
      <td className="px-3 py-2">
        <div className="font-medium text-ink-50">{row.accountName ?? "—"}</div>
        <div className="mt-0.5 text-[11px] text-ink-300">
          #{row.vid}
          {row.postcode ? ` · ${row.postcode}` : ""}
        </div>
      </td>
      <td className="px-3 py-2 text-ink-100">{row.technicianName ?? "—"}</td>
      <td className="px-3 py-2 text-ink-200">{expected}</td>
      <td className="px-3 py-2 text-ink-200" title={row.lastContactedAt ?? ""}>
        {fmtRelative(row.lastContactedAt)}
      </td>
      <td className="px-3 py-2">
        {editing ? (
          <div className="flex flex-col gap-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full min-w-[180px] rounded border border-ink-500 bg-ink-900 px-2 py-1 text-[12px] text-ink-50 focus:border-slack-aubergineLight focus:outline-none"
              autoFocus
            />
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => void saveM.mutate(draft)}
                disabled={saveM.isPending}
                className="rounded border border-ink-500 px-2 py-0.5 text-[11px] text-ink-100 hover:border-emerald-500 hover:text-emerald-200 disabled:opacity-50"
              >
                {saveM.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(row.paymentFollowupNote ?? "");
                  setEditing(false);
                }}
                disabled={saveM.isPending}
                className="rounded border border-ink-500 px-2 py-0.5 text-[11px] text-ink-300 hover:border-slack-pink hover:text-slack-pink disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraft(row.paymentFollowupNote ?? "");
              setEditing(true);
            }}
            className="w-full min-w-[180px] rounded border border-transparent px-2 py-1 text-left text-[12px] text-ink-100 hover:border-ink-500 hover:bg-ink-800/60"
            title={row.paymentFollowupNote ? `Last edited ${fmtRelative(row.paymentFollowupNoteUpdatedAt)}${row.paymentFollowupNoteActor ? ` by ${row.paymentFollowupNoteActor}` : ""}` : "Add a note"}
          >
            {row.paymentFollowupNote ? (
              <span className="whitespace-pre-wrap text-ink-50">{row.paymentFollowupNote}</span>
            ) : (
              <span className="italic text-ink-300">+ add note</span>
            )}
          </button>
        )}
      </td>
      <td
        className={`px-3 py-2 text-right tabular-nums font-semibold ${
          pastTint ? "text-orange-300" : "text-ink-50"
        }`}
      >
        {fmtGbp(row.outstanding)}
        <div className="text-[10px] font-normal text-ink-300">
          of {fmtGbp(row.totalCost)}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        {row.cardId !== null ? (
          <button
            type="button"
            onClick={() => onOpenCard(row.cardId!)}
            className="rounded border border-ink-500 px-2 py-1 text-[11px] text-ink-100 hover:border-slack-aubergineLight hover:text-ink-50"
          >
            Open card
          </button>
        ) : (
          <span className="text-[11px] text-ink-300" title="No kanban card linked">
            no card
          </span>
        )}
      </td>
    </tr>
  );
}

/** Light-weight separate hook for the top-bar badge count — fetches the
 *  same endpoint but doesn't render the full panel. Badge counts ONLY
 *  past-jobs-outstanding (i.e. work already started/done and still
 *  unpaid) — future bookings are visible inside the panel but don't
 *  inflate the alert number. */
export function useAwaitingPaymentsCount(): number {
  const q = useQuery({
    queryKey: ["awaitingPayments"],
    queryFn: fetchAwaitingPayments,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const d = q.data;
  if (!d) return 0;
  return d.outstanding.count;
}
